import type { Feature, MultiPolygon, Polygon } from 'geojson'
import type { LngLatCoord } from '@gpx-spa/geo-domain'
import { bufferLineStringMeters } from './buffer-line-string-meters.js'
import { simplifyLineStringCoordinates } from './simplify-line-string-coordinates.js'

/** One route alternative: raw polyline + corridor settings. */
export type CorridorBufferPathInput = {
  pathId: string
  raw: LngLatCoord[]
  qualityPercent: number
  radiusMeters: number
}

/** Simplified line + buffered polygon for one path. */
export type CorridorBufferPathResult = {
  pathId: string
  simplified: LngLatCoord[]
  buffer: Feature<Polygon | MultiPolygon> | null
}

/**
 * Pure sync: simplify + Turf buffer per path (no memoization).
 * Same geometry as {@link createLineBufferGeometryCache} per-path calls.
 *
 * Naming in this package:
 * - `computeCorridorBuffersSync` — pure, runs on caller thread (main or inside a worker).
 * - `computeCorridorBuffers` — public async: worker when available, else delegates here on main.
 * - Worker RPC: `GeoComputeWorkerApi.computeCorridorBuffers` — same work, exposed for Comlink.
 */
export function computeCorridorBuffersSync(
  paths: CorridorBufferPathInput[]
): CorridorBufferPathResult[] {
  const out: CorridorBufferPathResult[] = []
  for (const it of paths) {
    const { pathId, raw, qualityPercent, radiusMeters } = it
    if (!raw || raw.length < 2) {
      out.push({ pathId, simplified: raw ?? [], buffer: null })
      continue
    }
    const simplified =
      qualityPercent >= 100
        ? raw
        : (simplifyLineStringCoordinates(raw as [number, number][], qualityPercent) as LngLatCoord[])
    const buffer = radiusMeters <= 0 ? null : bufferLineStringMeters(simplified, radiusMeters)
    out.push({ pathId, simplified, buffer })
  }
  return out
}
